import { useEffect, useRef, useState } from "react";
import { buildShareText, copyToClipboard } from "../lib/shareCard";
import { useActiveLoop, type ScoreSource } from "../state/loops";

/**
 * One-tap share control (spec §16). Copies the active Loop's current-run
 * Wordle-style result to the clipboard with a brief confirmation. Disabled until
 * at least one Round has been played. Loop-aware: shares whichever Loop is active.
 */
export function ShareButton() {
  const activeLoop = useActiveLoop();

  // Trade's scoreboard is null (CONTEXT.md "Loop"): it is scored on Account
  // balance, not hit rate, so there is no run to share. Today this is also
  // moot because TradeStats never renders a ShareButton, but that invariant
  // lives there, not here, so make the Trade case explicit on its own terms.
  if (activeLoop.scoreboard === null) return null;
  return <ShareRun source={activeLoop.scoreboard} label={activeLoop.shareLabel} />;
}

/**
 * The control itself, over one scored Loop's run. Split out so the store
 * subscription is unconditional even though Trade renders no share control.
 */
function ShareRun({ source, label }: { source: ScoreSource; label: string }) {
  const score = source((s) => s.score);
  const bestStreak = source((s) => s.streak.best);

  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const onShare = async () => {
    const text = buildShareText({
      label,
      correct: score.correct,
      played: score.played,
      bestStreak,
    });
    if (await copyToClipboard(text)) {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <button
      type="button"
      onClick={onShare}
      disabled={score.played === 0}
      className="flex min-h-8 w-fit items-center rounded border border-muted/30 px-3 py-1 text-muted transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
    >
      {copied ? "copied!" : "Copy result"}
    </button>
  );
}
